// React
import { useState, useEffect } from "react"

// UI
import { Button } from "@/components/Button"
import { Tooltip } from "@/components/Tooltip"
import { Heading } from "@/components/Typography"

// DateTime
import { useLocalizedDateTime } from "@/lib/dates"

// Language
import { useLang } from "@/context/lang"

// Virtual PWA modules
import * as PWAInfo from "virtual:pwa-info"

// Env variables
import {
	// Vercel
	VERCEL_ENV,
	VERCEL_DOMAIN,
	VERCEL_GIT_COMMIT_SHA,
	VERCEL_GIT_COMMIT_MESSAGE,
	VERCEL_GIT_COMMIT_AUTHOR_NAME,
	GRAPHQL_URL,
	API_URL,

	// I18n
	DEFAULT_LANG,
	DEFAULT_TIMEZONE,

	// External
	EXTERNAL_PLATFORM_PRIVACY_POLICY,
	EXTERNAL_PLATFORM_INFO_CENTER,
	EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY,
	EXTERNAL_PLATFORM_ZONHUB_FINANCE,
	EXTERNAL_PLATFORM_ZONHUB_INVESTMENT,
	EXTERNAL_PLATFORM_ADMIN_PANEL,

	// Server
	SERVER_CSP_NONCE,
	MOCK_BACKEND,
	STORYBOOK_API_URL,
	JEST_WORKER_ID,
	DEBUG_I18N,

	// Integrations
	SENTRY_DSN,
	SENTRY_DEBUG,
	SENTRY_ENABLED,
	GOOGLE_MAPS_API_KEY,
	GOOGLE_MAPS_ID,
	GTM_CONTAINER_ID,
	FRESHDESK_WIDGET_ID,

	// Auth
	AUTH_LOGOUT_URL,
	AUTH_LOGIN_URL,
	AUTH_MFA_URL,
} from "@/lib/env"
import { CardBody, CardWrapper } from "@/components/Card"

/**
 * Info
 * @returns
 */
export function Info() {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Translate
	const { lang, formatCurrency } = useLang()

	// Template
	return (
		<>
			<CardWrapper className="m-4">
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-3 sm:truncate"
						data-testid="investments.fiscal.header"
					>
						Environment Variables
					</Heading>
					<div className="p-5">
						<Heading as="h5" className="mb-3 sm:truncate">
							Localization
						</Heading>
						<Tooltip content="Apps default language">
							<div className="grid grid-cols-2">
								<h2>DEFAULT_LANG</h2>
								<p>{DEFAULT_LANG}</p>
							</div>
						</Tooltip>

						<Tooltip content="Apps default timezone">
							<div className="grid grid-cols-2">
								<h2>DEFAULT_TIMEZONE</h2>
								<p>{DEFAULT_TIMEZONE}</p>
							</div>
						</Tooltip>
						<Tooltip content="Users active language">
							<div className="grid grid-cols-2">
								<h2>Active language</h2>
								<p>{lang}</p>
							</div>
						</Tooltip>
						<Tooltip content="Date string localised using users preferred language">
							<div className="grid grid-cols-2">
								<h2>Localized date (month)</h2>
								<p>
									{DateTime.now().toLocaleString({
										month: "long",
									})}
								</p>
							</div>
						</Tooltip>
						<Tooltip content="Apps default timezone">
							<div className="grid grid-cols-2">
								<h2>Localized currency</h2>
								<p>{formatCurrency(1000000000.0)}</p>
							</div>
						</Tooltip>

						<Heading as="h5" className="mb-3 sm:truncate">
							Deployment
						</Heading>

						<Tooltip content="The Environment that the app is deployed and running on. The value can be either production, preview, or development.">
							<div className="grid grid-cols-2">
								<h2>VERCEL_ENV</h2>
								<p>{VERCEL_ENV || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The MAIN domain name that is assigned as an alias to the deployment URL.">
							<div className="grid grid-cols-2">
								<h2>VERCEL_DOMAIN</h2>
								<p>{VERCEL_DOMAIN || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The git SHA of the commit the deployment was triggered by. Example: fa1eade47b73733d6312d5abfad33ce9e4068081.">
							<div className="grid grid-cols-2">
								<h2>VERCEL_GIT_COMMIT_SHA</h2>
								<p>{VERCEL_GIT_COMMIT_SHA || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The message attached to the commit the deployment was triggered by. Example: Update about page.">
							<div className="grid grid-cols-2">
								<h2>VERCEL_GIT_COMMIT_MESSAGE</h2>
								<p>{VERCEL_GIT_COMMIT_MESSAGE || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The name attached to the author of the commit that the project was deployed by. Example: John Doe.">
							<div className="grid grid-cols-2">
								<h2>VERCEL_GIT_COMMIT_AUTHOR_NAME</h2>
								<p>{VERCEL_GIT_COMMIT_AUTHOR_NAME || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="Nonce value for CSP (Content Security Policy) headers.">
							<div className="grid grid-cols-2">
								<h2>SERVER_CSP_NONCE</h2>
								<p>{SERVER_CSP_NONCE || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="Indicates whether the app is using a mock backend.">
							<div className="grid grid-cols-2">
								<h2>MOCK_BACKEND</h2>
								<p>{MOCK_BACKEND || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The API URL used for Storybook integration.">
							<div className="grid grid-cols-2">
								<h2>STORYBOOK_API_URL</h2>
								<p>{STORYBOOK_API_URL || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The Jest worker ID used for test runs.">
							<div className="grid grid-cols-2">
								<h2>JEST_WORKER_ID</h2>
								<p>{JEST_WORKER_ID || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="Indicates whether i18n debug mode is enabled.">
							<div className="grid grid-cols-2">
								<h2>DEBUG_I18N</h2>
								<p>{DEBUG_I18N || "n/a"}</p>
							</div>
						</Tooltip>

						<Heading as="h5" className="mb-3 sm:truncate">
							Integrations
						</Heading>
						<Tooltip content="Is Sentry enabled?">
							<div className="grid grid-cols-2">
								<h2>SENTRY_ENABLED</h2>
								<p>
									{SENTRY_ENABLED === "true"
										? "true"
										: "false"}
								</p>
							</div>
						</Tooltip>
						<Tooltip content="Indicates whether Sentry debug mode is enabled.">
							<div className="grid grid-cols-2">
								<h2>SENTRY_DEBUG</h2>
								<p>
									{SENTRY_DEBUG === "true" ? "true" : "false"}
								</p>
							</div>
						</Tooltip>
						<Tooltip content="The Sentry DSN for error monitoring.">
							<div className="grid grid-cols-2">
								<h2>SENTRY_DSN</h2>
								<p>{SENTRY_DSN || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The Google Maps API key for embedding maps.">
							<div className="grid grid-cols-2">
								<h2>GOOGLE_MAPS_API_KEY</h2>
								<p>{GOOGLE_MAPS_API_KEY || "n/a"}</p>
							</div>
						</Tooltip>

						<Tooltip content="The Google Maps ID for customizing map styling.">
							<div className="grid grid-cols-2">
								<h2>GOOGLE_MAPS_ID</h2>
								<p>{GOOGLE_MAPS_ID || "n/a"}</p>
							</div>
						</Tooltip>
						<Tooltip content="The Google Tag Manager container ID.">
							<div className="grid grid-cols-2">
								<h2>GTM_CONTAINER_ID</h2>
								<p>{GTM_CONTAINER_ID || "n/a"}</p>
							</div>
						</Tooltip>
						<Tooltip content="Freshdesk widget ID">
							<div className="grid grid-cols-2">
								<h2>FRESHDESK_WIDGET_ID</h2>
								<p>{FRESHDESK_WIDGET_ID || "n/a"}</p>
							</div>
						</Tooltip>

						<Heading as="h5" className="mb-3 sm:truncate">
							Links
						</Heading>

						<Tooltip content="The base URL for the API services.">
							<div className="grid grid-cols-2">
								<h2>API_URL</h2>
								<p>
									{API_URL ? (
										<a
											href={API_URL}
											target="_blank"
											rel="noreferrer"
										>
											{API_URL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="The GraphQL API endpoint used by the app.">
							<div className="grid grid-cols-2">
								<h2>GRAPHQL_URL</h2>
								<p>
									{GRAPHQL_URL ? (
										<a
											href={GRAPHQL_URL}
											target="_blank"
											rel="noreferrer"
										>
											{GRAPHQL_URL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication login process.">
							<div className="grid grid-cols-2">
								<h2>AUTH_LOGIN_URL</h2>
								<p>
									{AUTH_LOGIN_URL ? (
										<a
											href={AUTH_LOGIN_URL}
											target="_blank"
											rel="noreferrer"
										>
											{AUTH_LOGIN_URL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication logout process.">
							<div className="grid grid-cols-2">
								<h2>AUTH_LOGOUT_URL</h2>
								<p>
									{AUTH_LOGOUT_URL ? (
										<a
											href={AUTH_LOGOUT_URL}
											target="_blank"
											rel="noreferrer"
										>
											{AUTH_LOGOUT_URL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication MFA process.">
							<div className="grid grid-cols-2">
								<h2>AUTH_MFA_URL</h2>
								<p>
									{AUTH_MFA_URL ? (
										<a
											href={AUTH_MFA_URL}
											target="_blank"
											rel="noreferrer"
										>
											{AUTH_MFA_URL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's privacy policy.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_PRIVACY_POLICY</h2>
								<p>
									{EXTERNAL_PLATFORM_PRIVACY_POLICY ? (
										<a
											href={
												EXTERNAL_PLATFORM_PRIVACY_POLICY
											}
											target="_blank"
											rel="noreferrer"
										>
											{EXTERNAL_PLATFORM_PRIVACY_POLICY}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>
						<Tooltip content="The GraphQL API endpoint used by the app.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_ADMIN_PANEL</h2>
								<p>
									{EXTERNAL_PLATFORM_ADMIN_PANEL ? (
										<a
											href={EXTERNAL_PLATFORM_ADMIN_PANEL}
											target="_blank"
											rel="noreferrer"
										>
											{EXTERNAL_PLATFORM_ADMIN_PANEL}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's information center.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_ZONHUB_INVESTMENT</h2>
								<p>
									{EXTERNAL_PLATFORM_ZONHUB_INVESTMENT ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_INVESTMENT
											}
											target="_blank"
											rel="noreferrer"
										>
											{
												EXTERNAL_PLATFORM_ZONHUB_INVESTMENT
											}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's information center.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_INFO_CENTER</h2>
								<p>
									{EXTERNAL_PLATFORM_INFO_CENTER ? (
										<a
											href={EXTERNAL_PLATFORM_INFO_CENTER}
											target="_blank"
											rel="noreferrer"
										>
											{EXTERNAL_PLATFORM_INFO_CENTER}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's information center.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY</h2>
								<p>
									{EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY
											}
											target="_blank"
											rel="noreferrer"
										>
											{
												EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY
											}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's information center.">
							<div className="grid grid-cols-2">
								<h2>EXTERNAL_PLATFORM_ZONHUB_FINANCE</h2>
								<p>
									{EXTERNAL_PLATFORM_ZONHUB_FINANCE ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_FINANCE
											}
											target="_blank"
											rel="noreferrer"
										>
											{EXTERNAL_PLATFORM_ZONHUB_FINANCE}
										</a>
									) : (
										"n/a"
									)}
								</p>
							</div>
						</Tooltip>

						<Button
							onClick={() => {
								throw new Error("Test throw error")
							}}
						>
							Test throw error
						</Button>
					</div>
				</CardBody>
			</CardWrapper>
			<CardWrapper className="m-4">
				<CardBody>
					<Heading as="h5" className="mb-3 sm:truncate">
						Service Worker Testing
					</Heading>
					<ServiceWorkerTester />

					<Heading as="h5" className="mb-3 sm:truncate">
						PWA Info
					</Heading>

					<div className="max-h-[500px] overflow-y-auto">
						<pre>{JSON.stringify(PWAInfo, null, 2)}</pre>
					</div>
				</CardBody>
			</CardWrapper>
		</>
	)
}

/**
 * ServiceWorkerTester Component
 * Provides testing functionality for service worker updates
 */
function ServiceWorkerTester() {
	const [status, setStatus] = useState<string>("")
	const [statusType, setStatusType] = useState<"success" | "error" | "info">(
		"info",
	)
	const [logs, setLogs] = useState<string[]>([])
	const [swRegistration, setSwRegistration] =
		useState<ServiceWorkerRegistration | null>(null)

	const log = (message: string) => {
		const timestamp = new Date().toLocaleTimeString()
		const logMessage = `[${timestamp}] ${message}`
		setLogs((prev) => [...prev, logMessage])
		console.log(logMessage)
	}

	const updateStatus = (
		message: string,
		type: "success" | "error" | "info" = "info",
	) => {
		setStatus(message)
		setStatusType(type)
	}

	useEffect(() => {
		// Check if service worker is supported
		if ("serviceWorker" in navigator) {
			log("Service Worker is supported")

			// Register service worker
			navigator.serviceWorker
				.register("/sw.js", { scope: "/" })
				.then((registration) => {
					log("Service Worker registered successfully")
					updateStatus("Service Worker registered successfully")
					setSwRegistration(registration)

					// Check for updates
					registration.addEventListener("updatefound", () => {
						log("Service Worker update found!")
						updateStatus("Service Worker update found!", "info")
					})

					// Listen for service worker state changes
					registration.addEventListener("statechange", () => {
						log(
							`Service Worker state changed to: ${registration.active?.state || "unknown"}`,
						)
					})
				})
				.catch((error) => {
					log(`Service Worker registration failed: ${error}`)
					updateStatus(`Service Worker registration failed: ${error}`)
				})
		} else {
			log("Service Worker is not supported")
			updateStatus("Service Worker is not supported", "error")
		}

		// Listen for messages from service worker
		navigator.serviceWorker.addEventListener("message", (event) => {
			log(`Message from Service Worker: ${JSON.stringify(event.data)}`)
		})
	}, [])

	const checkForUpdates = () => {
		if (swRegistration) {
			log("Checking for service worker updates...")
			swRegistration
				.update()
				.then(() => {
					log("Update check completed")
					updateStatus("Update check completed", "success")
				})
				.catch((error) => {
					log(`Update check failed: ${error}`)
					updateStatus(`Update check failed: ${error}`, "error")
				})
		} else {
			log("No service worker registration found")
			updateStatus("No service worker registration found", "error")
		}
	}

	const reloadPage = () => {
		log("Reloading page...")
		window.location.reload()
	}

	const clearCache = async () => {
		if ("caches" in window) {
			log("Clearing all caches...")
			try {
				const cacheNames = await caches.keys()
				await Promise.all(
					cacheNames.map(async (cacheName) => {
						log(`Deleting cache: ${cacheName}`)
						return caches.delete(cacheName)
					}),
				)
				log("All caches cleared")
				updateStatus("All caches cleared", "success")
			} catch (error) {
				log(`Cache clearing failed: ${error}`)
				updateStatus(`Cache clearing failed: ${error}`, "error")
			}
		} else {
			log("Cache API not supported")
			updateStatus("Cache API not supported", "error")
		}
	}

	const clearLogs = () => {
		setLogs([])
	}

	return (
		<div className="space-y-4">
			{/* Status Display */}
			{status && (
				<div
					className={`rounded-md p-3 ${
						statusType === "success"
							? "border border-green-200 bg-green-100 text-green-800"
							: statusType === "error"
								? "border border-red-200 bg-red-100 text-red-800"
								: "border border-blue-200 bg-blue-100 text-blue-800"
					}`}
				>
					{status}
				</div>
			)}

			{/* Action Buttons */}
			<div className="flex flex-wrap gap-2">
				<Button
					onClick={checkForUpdates}
					className="bg-blue-600 hover:bg-blue-700"
				>
					Check for Updates
				</Button>
				<Button
					onClick={reloadPage}
					className="bg-gray-600 hover:bg-gray-700"
				>
					Reload Page
				</Button>
				<Button
					onClick={clearCache}
					className="bg-orange-600 hover:bg-orange-700"
				>
					Clear Cache
				</Button>
				<Button
					onClick={clearLogs}
					className="bg-purple-600 hover:bg-purple-700"
				>
					Clear Logs
				</Button>
			</div>

			{/* Service Worker Info */}
			<div className="grid grid-cols-2 gap-4 text-sm">
				<div>
					<h3 className="font-semibold">Service Worker Status</h3>
					<p>
						Supported: {"serviceWorker" in navigator ? "Yes" : "No"}
					</p>
					<p>Registered: {swRegistration ? "Yes" : "No"}</p>
					<p>State: {swRegistration?.active?.state || "Unknown"}</p>
				</div>
				<div>
					<h3 className="font-semibold">Cache Status</h3>
					<p>
						Cache API:{" "}
						{"caches" in window ? "Supported" : "Not Supported"}
					</p>
					<p>Logs: {logs.length} entries</p>
				</div>
			</div>

			{/* Logs Display */}
			<div className="max-h-64 overflow-y-auto rounded-md bg-gray-100 p-3">
				<h3 className="mb-2 font-semibold">Logs</h3>
				{logs.length === 0 ? (
					<p className="text-sm text-gray-500">
						No logs yet. Try checking for updates or clearing cache.
					</p>
				) : (
					<div className="space-y-1 text-xs">
						{logs.map((log, index) => (
							<div key={index} className="font-mono">
								{log}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
